/**
 * RmsProviderSignIn.js
 * 
 * Sign-in page for RMS (Referral Management System) provider users.
 * 
 * Design:
 *   Uses the same compact, centered card layout as ServiceUserSignIn.js for
 *   visual consistency across the RMS product. The card is max-w-sm (~384px)
 *   centered on a clean gray background.
 * 
 * Authentication:
 *   - Primary: Email and password sign-in
 *   - Secondary: Microsoft SSO (for NHS organisations with tenant configuration)
 *   - MFA support via SMS verification
 *   - Password reset functionality
 * 
 * RMS Provider user types:
 *   - RMS Clinician: Manages referrals, views triage data, assigns priority
 * 
 * Post-authentication:
 *   Users are redirected to /rms/provider/dashboard
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { auth, analytics, functions, db } from '../../../firebase';
// logEvent also feeds the first-party stream via the GA4 bridge;
// the signature and GA4 behaviour are unchanged.
import { logEvent } from '../../../analytics/ga4';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import ResetPasswordDialog from '../../../components/auth/ResetPasswordDialog';
import MfaDialog from '../../../components/auth/MfaDialog';
import useEmailPasswordSignIn from '../../../hooks/auth/useEmailPasswordSignIn';
import MicrosoftButton from '../../../components/auth/MicrosoftButton';
import { 
  buildMicrosoftProvider,
  // isProjectAllowedToUseMicrosoftSSO 
} from '../../../utils/auth/microsoftProvider';
import { signInWithPopup, signInWithRedirect, signOut } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import providerLogo from '../../assets/images/logos/lcs_logo.png';

export default function RmsProviderSignIn() {
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Track Microsoft SSO flow state independently of email/password loading
  const [msLoading, setMsLoading] = useState(false);

  const handleForgotPasswordClose = () => setShowForgotPassword(false);

  // MFA dialog state
  const [showMFAPrompt, setShowMFAPrompt] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [supportCtx, setSupportCtx] = useState({ userId: '', organisation: '' });

  // Simple email validation - checks for basic email format
  const isValidEmail = (emailStr) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailStr);
  };

  // Wrong-account guard for the email/password path. The shared sign-in hook
  // routes any successfully authenticated user onward, so an account that is
  // not an RMS provider account here (another product's account, or one
  // provisioned for a different environment whose email also exists in this
  // project) was sent into RMS flows it was never part of - most visibly the
  // invite password-setup page, which dead-ends. Mirror the Microsoft SSO
  // guard below: verify the RMS provider claim before following any
  // post-auth route; otherwise sign out and say so on the form. The hook
  // keeps the user signed in at every routing point, and invited RMS
  // providers carry the claim before completing setup, so this never blocks
  // a legitimate journey.
  const ensureRmsProviderAccount = async () => {
    let isRmsProvider = false;
    try {
      const tokenResult = await auth.currentUser?.getIdTokenResult();
      isRmsProvider = tokenResult?.claims?.accountType === 'RMS Provider User';
    } catch (_) {
      // Unreadable token -> treat as not an RMS provider account.
    }
    if (isRmsProvider) return true;
    try {
      await signOut(auth);
    } catch (_) {}
    setError(
      'Incorrect account for this portal. Please check you are signing in with your RMS provider account for this environment.',
    );
    return false;
  };

  // Centralized sign-in flow (persistence, email verification, temp password reroute, MFA, errors)
  // Note: RMS Clinicians route to /rms/provider/dashboard instead of /dashboard
  const { 
    isLoading: hookLoading, 
    error: hookError, 
    errorCode: hookErrorCode, 
    mfaState, 
    startSignIn, 
    completeMfa, 
    resetRecaptcha, 
    cancelMfa, 
    resendMfa 
  } = useEmailPasswordSignIn({
    entryAccountType: 'Provider',
    onRoute: async (route, payload) => {
      // Every post-auth route is gated on the account actually being an RMS
      // provider account (see ensureRmsProviderAccount above).
      if (!(await ensureRmsProviderAccount())) return;
      if (route === 'verifyEmail') return navigate('/verify-email');
      if (route === 'passwordSetup') {
        const encodedEmail = encodeURIComponent(btoa(payload.email));
        return navigate(`/rms/provider/set-up-password?setup=true&e=${encodedEmail}`);
      }
      if (route === 'mfaSetup') return navigate('/set-up-mfa');
      if (route === 'dashboard') {
        // When the sign-in hook determines setup is complete and the user is
        // ready for the dashboard, finalise the invite by transitioning the
        // provider profile from 'invited' to 'active'. This is idempotent so
        // it's safe to call on every dashboard route (already-active = no-op).
        try {
          const tokenResult = await auth.currentUser?.getIdTokenResult();
          const orgId = tokenResult?.claims?.rmsOrgId;
          if (orgId && tokenResult?.claims?.accountType === 'RMS Provider User') {
            const completeSetup = httpsCallable(functions, 'rmsCompleteProviderSetup');
            await completeSetup({ orgId });
          }
        } catch (e) {
          console.warn('[RmsProviderSignIn] rmsCompleteProviderSetup failed (non-blocking):', e);
        }
        return navigate('/rms/provider/dashboard');
      }
    }
  });

  // Open MFA dialog when required by sign-in hook
  useEffect(() => {
    if (mfaState.required) setShowMFAPrompt(true);
  }, [mfaState.required]);

  // Handle success messages from URL parameters
  useEffect(() => {
    const message = searchParams.get('message');
    const encodedEmail = searchParams.get('e');
    const urlEmail = searchParams.get('email');
    const ssoError = searchParams.get('sso_error');
    
    if (message === 'setup_complete') {
      setSuccessMessage('Your account has been set up successfully! Please sign in with your new password.');
    } else if (message === 'account_already_setup') {
      setSuccessMessage('Your account is already set up. Please sign in with your password.');
    }

    // Email prefill is independent of the message type — it fires for any URL
    // carrying ?e= (base64) or ?email= (plain), including the new email-link flow
    // where no `message` param is present (e.g. "Account Setup Complete" email).
    // Prefer base64 param `e`; fall back to url-encoded `email`.
    if (encodedEmail) {
      try {
        const decoded = atob(encodedEmail);
        setFormData(prev => ({ ...prev, email: decoded }));
      } catch (_) {}
    } else if (urlEmail) {
      setFormData(prev => ({ ...prev, email: decodeURIComponent(urlEmail) }));
    }

    // Microsoft-first invite onboarding errors
    if (ssoError) {
      const friendly = (() => {
        if (ssoError === 'invite_expired') return 'Your invite has expired. Please contact your organisation administrator.';
        if (ssoError === 'invite_not_found') return 'No active invite was found for your account. Please contact your organisation administrator.';
        if (ssoError === 'blocked_wrong_tenant') return 'You must sign in with your NHS Microsoft account for your organisation.';
        if (ssoError === 'blocked_uninvited_email') return 'Your account is not yet enabled. Please contact your organisation administrator.';
        if (ssoError === 'blocked_missing_tenant_claim') return 'We could not verify your organisation tenant. Please contact your organisation administrator.';
        return 'Sign in with Microsoft failed. Please contact your organisation administrator.';
      })();
      setError(friendly);
    }
  }, [searchParams]);

  // Gather User ID and claims for support emails
  useEffect(() => {
    const u = auth?.currentUser;
    if (!u) return;
    setSupportCtx((prev) => ({ ...prev, userId: u.uid }));
    try {
      u.getIdTokenResult(true).then((res) => {
        const org = res?.claims?.rmsOrgId || '';
        setSupportCtx({ userId: u.uid, organisation: org });
      }).catch(() => {});
    } catch (_) {}
  }, []);

  // Password input change handler
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData(prev => ({
      ...prev,
      password: newPassword
    }));
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    await startSignIn(formData.email, formData.password);
  };

  // Microsoft SSO configuration
  const canShowMicrosoftSSO = false; // isProjectAllowedToUseMicrosoftSSO();
  const hidePasswordSignInUI = false;
  const msTestTenantRaw = (import.meta.env.VITE_MS_TEST_TENANT || '').trim();
  const msTestTenant = msTestTenantRaw.replace(/^['"]|['"]$/g, '');
  const forceMicrosoftRedirect = msTestTenant === 'consumers';

  // Shared post-Microsoft-sign-in logic
  // Note: RMS Clinicians go to /rms/provider/dashboard
  const postMicrosoftSignIn = async (screen) => {
    const tokenResult = await auth.currentUser.getIdTokenResult(true);

    // For invited Provider users who haven't completed setup yet, finalize now
    const setupComplete =
      tokenResult.claims?.hasCompletedSetup === true ||
      tokenResult.claims?.hasSetOwnPassword === true;
    if (tokenResult.claims?.accountType === 'RMS Provider User' && !setupComplete) {
      console.log('[TD-SSO] RmsProviderSignIn: setup incomplete -> calling completeProviderUserSSOSetup', {
        screen,
        uid: auth?.currentUser?.uid,
        authMode: tokenResult?.claims?.authMode,
      });
      const completeSSO = httpsCallable(functions, 'completeProviderUserSSOSetup');
      await completeSSO();
      await auth.currentUser.getIdTokenResult(true);
    }

    // Post-sign-in guard: require Provider claims and an rmsProviderUsers doc
    let refreshed = await auth.currentUser.getIdTokenResult(true);
    let providerOk = refreshed.claims?.accountType === 'RMS Provider User';
    const orgId = refreshed.claims?.rmsOrgId;

    // Query the org-scoped rmsProviderUsers subcollection by accountId
    let hasProviderProfile = false;
    if (orgId) {
      try {
        const providerQuery = query(
          collection(db, 'rms_organisations', orgId, 'rmsProviderUsers'),
          where('accountId', '==', auth.currentUser.uid)
        );
        const providerSnap = await getDocs(providerQuery);
        hasProviderProfile = !providerSnap.empty;
      } catch (_) {}
    }

    if (!providerOk || !hasProviderProfile) {
      try {
        const consumeInvite = httpsCallable(functions, 'consumeProviderUserInvite');
        await consumeInvite();
        refreshed = await auth.currentUser.getIdTokenResult(true);
        providerOk = refreshed.claims?.accountType === 'RMS Provider User';
        if (refreshed.claims?.rmsOrgId) {
          const retryQuery = query(
            collection(db, 'rms_organisations', refreshed.claims.rmsOrgId, 'rmsProviderUsers'),
            where('accountId', '==', auth.currentUser.uid)
          );
          const retrySnap = await getDocs(retryQuery);
          hasProviderProfile = !retrySnap.empty;
        }
      } catch (e) {
        const msg = String(e?.message || '');
        const code =
          msg.includes('invite_expired') ? 'invite_expired'
            : msg.includes('invite_not_found') ? 'invite_not_found'
              : msg.includes('invite_org_not_supported') ? 'invite_org_not_supported'
                : 'invite_not_found';
        try {
          await signOut(auth);
        } catch (_) {}
        setError('Your account is not yet enabled. Please contact your organisation administrator.');
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('sso_error', code);
          window.history.replaceState({}, '', url.toString());
        } catch (_) {}
        return;
      }
    }

    console.log('[TD-SSO] RmsProviderSignIn: post-sign-in guard check', {
      screen,
      uid: auth?.currentUser?.uid,
      providerOk,
      hasProviderProfile,
      authMode: refreshed?.claims?.authMode,
    });

    if (!providerOk || !hasProviderProfile) {
      await signOut(auth);
      setError('Your account is not yet enabled. Please contact your organisation administrator.');
      logEvent(analytics, 'sso_guard_signout', {
        screen,
        reason: !providerOk ? 'missing_provider_claims' : 'missing_provider_profile',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      });
      return;
    }

    logEvent(analytics, 'sso_microsoft_success', {
      screen,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      tenant: import.meta.env.VITE_MS_TENANT_ID || msTestTenant || 'common',
    });

    // RMS Clinicians go to RMS provider dashboard
    console.log('[TD-SSO] RmsProviderSignIn: navigating to /rms/provider/dashboard', { screen, uid: auth?.currentUser?.uid });
    return window.location.assign('/rms/provider/dashboard');
  };

  // Microsoft SSO sign-in handler
  const handleMicrosoftSignIn = async () => {
    try {
      setError('');
      setMsLoading(true);
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const shouldRedirect = isMobile || (forceMicrosoftRedirect && !isLocalhost);

      logEvent(analytics, 'sso_microsoft_click', { 
        screen: 'RmsProviderSignIn',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        tenant: import.meta.env.VITE_MS_TENANT_ID || msTestTenant || 'common',
        isMobile,
        flow: shouldRedirect ? 'redirect' : 'popup',
      });

      const provider = buildMicrosoftProvider();
      if (shouldRedirect) {
        try {
          sessionStorage.setItem('__td_ms_redirect_started', String(Date.now()));
          sessionStorage.setItem('__td_ms_redirect_tenant', String(import.meta.env.VITE_MS_TENANT_ID || msTestTenant || 'common'));
        } catch (_) {}
        console.log('[MicrosoftSSO] starting redirect flow (RMS)', {
          shouldRedirect,
          msTestTenant,
          tenant: import.meta.env.VITE_MS_TENANT_ID || msTestTenant || 'common',
        });
        await signInWithRedirect(auth, provider);
        return;
      }

      await signInWithPopup(auth, provider);
      await postMicrosoftSignIn('RmsProviderSignIn');
    } catch (e) {
      let message = 'Sign in with Microsoft failed. Please try again.';
      if (e?.code === 'auth/account-exists-with-different-credential') {
        message = hidePasswordSignInUI
          ? 'We could not link your Microsoft sign-in to your account. Please contact your organisation administrator.'
          : 'An account already exists with the same email but different sign-in method. Sign in with your email/password once, then link Microsoft from Settings.';
      }
      setError(message);
      logEvent(analytics, 'sso_microsoft_error', {
        screen: 'RmsProviderSignIn',
        code: e?.code || 'unknown',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        tenant: import.meta.env.VITE_MS_TENANT_ID || msTestTenant || 'common',
        flow: forceMicrosoftRedirect ? 'redirect' : 'popup',
      });
    } finally {
      setMsLoading(false);
    }
  };

  // MFA dialog close handler
  const handleMFADialogClose = (open) => {
    setShowMFAPrompt(open);
    if (!open) {
      resetRecaptcha();
    }
  };

  // Environment banner logic - shows in dev/staging to help users identify environment
  const showEnvBanner = (import.meta.env.VITE_ENVIRONMENT === "development" || import.meta.env.VITE_ENVIRONMENT === "staging");
  const envBannerText = import.meta.env.VITE_ENVIRONMENT === "development" ? "Development Environment" : "Testing / Training Environment";
  const envBannerClass = import.meta.env.VITE_ENVIRONMENT === "development" 
    ? "bg-yellow-100 text-yellow-800 border-yellow-200" 
    : "bg-blue-100 text-blue-800 border-blue-200";

  // Check if form is valid for submission
  const isFormValid = isValidEmail(formData.email) && formData.password.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      {/* Compact centered card container - matches ServiceUserSignIn design */}
      <div className="w-full max-w-sm">
        {/* Environment banner - positioned above the card content */}
        {showEnvBanner && (
          <div className={`mb-10 px-4 py-2 rounded-lg border text-center text-sm font-medium ${envBannerClass}`}>
            {envBannerText}
          </div>
        )}

        {/* Logo - centered at top */}
        <div className="flex justify-center mb-10">
          <img src={providerLogo} alt="Lancashire and South Cumbria Logo" className="h-12 w-auto" />
        </div>

        {/* Card content - centered text for clean appearance */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Referral Management System Provider Portal</h1>
          <p className="text-gray-600 mb-8">Sign in to access your account.</p>

          {/* Microsoft SSO button - shown if organisation supports it */}
          {canShowMicrosoftSSO && (
            <>
              <div className="mb-4">
                <MicrosoftButton onClick={handleMicrosoftSignIn} loading={msLoading} />
              </div>
              {!hidePasswordSignInUI && (
                <div className="flex items-center my-6">
                  <div className="flex-grow h-px bg-gray-200" />
                  <span className="px-4 text-sm text-gray-500 uppercase tracking-wide">or</span>
                  <div className="flex-grow h-px bg-gray-200" />
                </div>
              )}
            </>
          )}

          {/* Email/Password sign-in form */}
          {!hidePasswordSignInUI && (
            <form onSubmit={handleSignIn} autoComplete="on" className="text-left">
              {/* Email input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="name@example.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                />
              </div>

              {/* Password input with show/hide toggle */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Enter your password"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <span className="relative h-5 w-5">
                      <EyeIcon
                        className={`absolute inset-0 h-5 w-5 transition-all duration-150 ${
                          showPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                        }`}
                      />
                      <EyeOffIcon
                        className={`absolute inset-0 h-5 w-5 transition-all duration-150 ${
                          showPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                        }`}
                      />
                    </span>
                  </button>
                </div>
              </div>

              {/* Sign in button */}
              <button
                type="submit"
                disabled={!isFormValid || hookLoading}
                className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all flex items-center justify-center ${
                  (!isFormValid || hookLoading)
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg'
                }`}
              >
                {hookLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          )}

          {/* Success message */}
          <div className={`transition-all duration-300 overflow-hidden ${successMessage ? 'max-h-24 mt-4' : 'max-h-0 mt-0'}`}>
            <div className={`text-sm text-green-700 bg-green-50 rounded-lg py-3 px-4 ${successMessage ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
              ✓ {successMessage}
            </div>
          </div>

          {/* Error message */}
          {(error || hookError) && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 rounded-lg py-3 px-4 text-left">
              {error || hookError}
              {hookErrorCode && (
                <span className="block text-xs mt-1 text-gray-500">Error code: {hookErrorCode}</span>
              )}
            </div>
          )}

          {/* Forgot password link */}
          {!hidePasswordSignInUI && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-primary hover:text-primary/80 underline"
              >
                Forgot your password?
              </button>
            </div>
          )}

          {/* Support footer */}
          <div className="mt-8 text-center">
            {(() => {
              const subject = 'Reporting Issue with ThinkDivergent RMS';
              const now = new Date();
              const day = now.getDate();
              const sfx = (d) => {
                if (d % 100 >= 11 && d % 100 <= 13) return 'th';
                switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
              };
              const easyTime = `${day}${sfx(day)} ${now.toLocaleString('en-GB', { month: 'long' })} ${now.getFullYear()} at ${now.toLocaleString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
              const isoTime = now.toISOString();
              const currentError = (error || hookError || '').toString();
              const supportCode = hookErrorCode || '';
              const lines = [
                'To ThinkDivergent Support Team,',
                '',
                'I would like to report an issue on the RMS platform.',
                '',
                '[Please describe briefly what you were doing and what happened]',
                '',
                'Helpful Information:',
                `- Screen: ${typeof window !== 'undefined' ? window.location.pathname : ''}`,
                `- URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
                `- Time: ${easyTime} (${isoTime})`,
                supportCtx.userId ? `- User ID: ${supportCtx.userId}` : undefined,
                supportCtx.organisation ? `- Organisation: ${supportCtx.organisation}` : undefined,
                supportCode ? `- Support/Error code: ${supportCode}` : undefined,
                currentError ? `- Message: ${currentError}` : undefined,
                `- Browser: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`
              ].filter(v => v != null).join('\n');
              const href = `mailto:support@thinkdivergent.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
              return (
                <p className="text-xs text-gray-500">
                  Need help? Contact your organisation administrator or{' '}
                  <a href={href} className="underline text-primary hover:text-primary/80">
                    report an issue
                  </a>
                </p>
              );
            })()}
          </div>

          {/* Footer partnership note */}
          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-600">
            <span>Made in partnership with</span>
            <img src="/td_logo_full_2025.png" alt="ThinkDivergent" className="h-6 w-auto opacity-90" />
          </div>
        </div>
      </div>

      {/* Reset Password Dialog */}
      <ResetPasswordDialog
        open={showForgotPassword}
        onOpenChange={handleForgotPasswordClose}
        initialEmail={formData.email}
        onSubmit={async (email) => {
          const sendProviderPasswordResetEmail = httpsCallable(functions, 'sendProviderPasswordResetEmail');
          await sendProviderPasswordResetEmail({ email });
          // userType lets the bridge resolve the audience to rms_provider;
          // without it the registry row would be labelled clinician.
          logEvent(analytics, 'password_reset_requested', { userType: 'rmsProvider' });
        }}
      />

      {/* MFA Dialog */}
      <MfaDialog
        open={showMFAPrompt}
        onOpenChange={handleMFADialogClose}
        mfaType={mfaState?.mfaType || 'phone'}
        maskedPhone={mfaState?.maskedPhone}
        isLoading={hookLoading}
        error={error || hookError}
        errorCode={hookErrorCode}
        sentAt={mfaState.sentAt}
        cooldownSeconds={30}
        onVerify={async (code) => {
          await completeMfa(code);
        }}
        onCancel={cancelMfa}
        onResend={async () => await resendMfa()}
        tooltipSide="right"
      />
    </div>
  );
}
